import _ from 'lodash';
import Vue from 'vue';
import statusConst from '@/consts/status.const.js';
import modeConst from '@/consts/mode.const';
import genericTabConst from '@/consts/genericTab.const';
import pixAxios from '@/axios/pixAxios.js';

const propertyModule = {
  namespaced: true,

  state: {
    property: null, // mutated by property tabs
    attributes: null,
    propertyTabsStatus: [],
    propertyStatus: statusConst.PENDING,
    originalProperty: null, // not mutated
    scopeProperty: null, // only mutated by the scope tab
    propertyDetailsProperty: null, // only mutated by the property details tab
    selectedTab: null,
    relationGroupsSections: null,
    openSubmitModal: false,
    revertSubmit: false,
    tabSaveStatus: {},
    approvalFn: {},
    willSubmitPromise: null,
    willSubmitPromiseResolve: null,
    willSubmitFromOtherSource: false, // We should set this property to true in case we want to implement another logic to the submit modal
  },

  actions: {
    async loadProperty({ commit, dispatch, rootGetters, getters }, propertyRowId) {
      const propertyUrl = `api/Property/GetPropertyById?propertyRowId=${propertyRowId}`;
      const attributesUrl = `api/Property/GetAttributesMaster?instance=${rootGetters['authModule/fullInstance']}&langCode=${rootGetters.langCode}`;

      commit('setPropertyStatus', statusConst.LOADING);
      try {
        const [{ data: property }, { data: attributes }] = await Promise.all([pixAxios.get(propertyUrl), pixAxios.get(attributesUrl)]);

        if (property && attributes) {
          commit('setAttributes', attributes);
          commit('setOriginalProperty', property);
          commit('setProperty', _.cloneDeep(property));
          commit('setScopeProperty', _.cloneDeep(property));
          commit('setPropertyDetailsProperty', _.cloneDeep(property));
          commit('setPropertyStatus', statusConst.SUCCESS);
          return property;
        } else {
          commit('setPropertyStatus', statusConst.FAIL);
          dispatch('toastModule/error', 'Error loading the subject property', { root: true });
        }
      } catch (error) {
        commit('setPropertyStatus', statusConst.FAIL);
        dispatch('toastModule/error', 'Error loading the subject property', { root: true });
      }
    },

    setProperty({ commit }, property) {
      commit('setProperty', property);
    },

    async loadPropertyTabs({ dispatch, rootGetters, getters }) {
      if (rootGetters['genericConfigModule/getGenericTabs'](modeConst.PROPERTY)?.length) return;
      try {
        const { data } = await pixAxios.get('api/GenericConfig/GetPropertyTabs', { params: { instance: rootGetters['authModule/fullInstance'], lang: rootGetters.langCode } });
        await dispatch('genericConfigModule/setGenericTabs', { data, mode: modeConst.PROPERTY }, { root: true });
      } catch (error) {
        dispatch('toastModule/error', 'Error loading the property tabs', { root: true });
      }
    },

    setPropertyTabsStatus({ commit }, payload) {
      commit('setPropertyTabsStatus', payload);
    },

    setRelationGroupsSections({ commit }, payload) {
      commit('setRelationGroupsSections', payload);
    },

    setWillSubmitPromise({ commit }) {
      commit('setWillSubmitPromise');
    },

    startSaveSelectedTab({ commit, getters }, payload) {
      commit('startSaveSelectedTab', {
        ...payload,
        selectedTab: getters.getSelectedTab,
      });
    },

    startRevertSubmitSelectedTab({ commit, getters }, payload) {
      commit('setStartRevertSubmitSelectedTab', {
        ...payload,
        selectedTab: getters.getSelectedTab,
      });
    },

    finishSaveSelectedTab({ commit, getters }) {
      commit('finishSaveSelectedTab', getters.getSelectedTab);
    },

    setApprovalFn({ commit }, payload) {
      commit('setApprovalFn', payload);
    },

    async setOpenSubmitModal({ commit }, { open, isRevertSubmit }) {
      commit('setOpenSubmitModal', open);
      commit('setRevertSubmitModal', !!isRevertSubmit);
    },

    setWillSubmitFromAnotherSource({ commit }, payload) {
      commit('setWillSubmitFromAnotherSource', payload);
    },
  },

  mutations: {
    setProperty(state, payload) {
      state.property = payload;
    },

    setScopeProperty(state, payload) {
      state.scopeProperty = payload;
    },

    setPropertyDetailsProperty(state, payload) {
      state.propertyDetailsProperty = payload;
    },

    setOriginalProperty(state, payload) {
      state.originalProperty = payload;
    },

    setAttributes(state, payload) {
      state.attributes = payload;
    },

    setPropertyStatus(state, payload) {
      state.propertyStatus = payload;
    },

    setRelationGroupsSections(state, payload) {
      state.relationGroupsSections = payload;
    },

    setPropertyTabsStatus(state, payload) {
      state.propertyTabsStatus = payload;
    },

    setSelectedTab(state, payload) {
      state.selectedTab = payload;
    },

    startSaveSelectedTab(state, { selectedTab, isSubmit, checkSubmit }) {
      Vue.set(state.tabSaveStatus, selectedTab, { isSubmit, checkSubmit });
    },

    setStartRevertSubmitSelectedTab(state, { selectedTab, checkRevertSubmit }) {
      Vue.set(state.tabSaveStatus, selectedTab, { checkRevertSubmit, isRevertSubmit: true });
    },

    finishSaveSelectedTab(state, selectedTab) {
      Vue.set(state.tabSaveStatus, selectedTab, false);
    },

    setApprovalFn(state, payload) {
      state.approvalFn[state.selectedTab] = payload;
    },

    setOpenSubmitModal(state, payload) {
      state.openSubmitModal = payload;
    },

    setRevertSubmitModal(state, payload) {
      state.revertSubmit = payload;
    },

    setWillSubmitPromise(state) {
      state.willSubmitPromise = new Promise((resolve) => {
        state.willSubmitPromiseResolve = resolve;
      });
    },

    setWillSubmitFromOtherSource(state, payload) {
      state.willSubmitFromOtherSource = payload;
    },
  },

  getters: {
    getProperty(state) {
      return state.property;
    },

    getScopeProperty(state) {
      return state.scopeProperty;
    },

    getPropertyDetailsProperty(state) {
      return state.propertyDetailsProperty;
    },

    getOriginalProperty(state) {
      return state.originalProperty;
    },

    getAttributes(state) {
      return state.attributes;
    },

    getPropertyStatus(state) {
      return state.propertyStatus;
    },

    getPropertyType(state) {
      return state?.property?.attributes?.find((attribute) => attribute.FeatureName === 'SubjectInfoPropertyType')?.StringValue;
    },

    getPropertyAttribute: (state) => (attributeName) => {
      return state?.property?.attributes.find((attribute) => attribute.FeatureName === attributeName);
    },

    getAttributeByFeatureName: (state) => (featureName) => {
      return {
        ...state.attributes.find((attribute) => attribute.FeatureName === featureName),
      };
    },

    isLoadingPropertyStatus(state) {
      return state.propertyStatus === statusConst.LOADING;
    },

    getRelationGroupsSections(state) {
      return state.relationGroupsSections;
    },

    getTabSectionsPercentages(state) {
      return state.property?.TabSectionsPercentages?.map((section) => {
        section.Section = state.relationGroupsSections?.find(({ SectionId }) => SectionId === section.IdSection)?.GroupId ?? section.Section;
        return section;
      });
    },

    getSelectedTab(state) {
      return state.selectedTab;
    },

    getPropertyTabConfig: (state, getters, rootState, rootGetters) => (tabId) => {
      return rootGetters['genericConfigModule/getGenericTabConfig'](tabId, modeConst.PROPERTY);
    },

    getPropertyTabConfigStatus: (state, getters, rootState, rootGetters) => (tabId) => {
      return rootGetters['genericConfigModule/getGenericTabConfigStatus'](tabId, modeConst.PROPERTY);
    },

    getTabsStatus(state) {
      return state.propertyTabsStatus;
    },

    getTabStatus: (state) => (tabId) => {
      return state.propertyTabsStatus?.find((tab) => tab.idTab === tabId) ?? [];
    },

    getProgressPercentage(state, getters, rootState, rootGetters) {
      return (idCategory, idSection = null) => {
        if (!state.property?.TabSectionsPercentages) return 0;
        if (idSection) {
          return formatPct(state.property.TabSectionsPercentages.find(({ IdSection }) => IdSection === idSection)?.ProgressPct);
        }
        const id2GetMethod = {
          // 5 is lease
          [genericTabConst.LEASE_COMPS]: () => rootGetters['comparablesModule/getSelectedComparablesByTab'](genericTabConst.LEASE_COMPS),
          // 6 is sales
          [genericTabConst.SALES_COMPS]: () => rootGetters['comparablesModule/getSelectedComparablesByTab'](genericTabConst.SALES_COMPS),
          // 8 is land
          [genericTabConst.LAND_COMPS]: () => rootGetters['comparablesModule/getSelectedComparablesByTab'](genericTabConst.LAND_COMPS),
          // 9 is ie
          [genericTabConst.IE_COMPS]: () => rootGetters['comparablesModule/getSelectedComparablesByTab'](genericTabConst.IE_COMPS),
          // 17 is lease
          [genericTabConst.RENT_SURVEY_COMPS]: () => rootGetters['comparablesModule/getSelectedComparablesByTab'](genericTabConst.RENT_SURVEY_COMPS),
        };
        if (id2GetMethod[idCategory]) {
          return formatPct(id2GetMethod[idCategory]().length > 2 ? 1 : 0);
        }

        const sections = state.property.TabSectionsPercentages.filter(({ IdCategory }) => IdCategory === idCategory);
        if (sections.length === 1) return formatPct(sections[0].ProgressPct);
        return sections.length ? formatPct(sections.reduce(({ ProgressPct: a }, { ProgressPct: b }) => (a + b) / sections.length)) : 0;
      };
    },

    getApprovalFn(state) {
      return state.approvalFn[state.selectedTab];
    },

    getOpenSubmitModal(state) {
      return state.openSubmitModal;
    },

    getRevertSubmitModal(state) {
      return state.revertSubmit;
    },

    getTabSaveStatus: (state) => (selectedTab) => {
      return state.tabSaveStatus[selectedTab];
    },

    getWillSubmitPromise(state) {
      return state.willSubmitPromise;
    },

    getWillSubmitPromiseResolve(state) {
      return state.willSubmitPromiseResolve;
    },

    getWillSubmitFromOtherSource(state) {
      return state.willSubmitFromOtherSource;
    },
  },
};
export default propertyModule;

const formatPct = (num) => {
  return num ? Math.floor(num * 100) : 0;
};
